import type { PlayerId } from "../core/ids.ts";
import type { Vector3 } from "../core/math.ts";

export const GAME_STATE_SCHEMA_VERSION = 3 as const;

export type PlayerLocomotionMode =
  | "grounded"
  | "jumping"
  | "falling"
  | "mantling"
  | "flying";

export interface PlayerLocomotionState {
  mode: PlayerLocomotionMode;
  grounded: boolean;
  groundNormal: Vector3;
  groundSlopeRadians: number;
  surfaceId: string | null;
  coyoteTicksRemaining: number;
  jumpBufferTicksRemaining: number;
  landingResponseTicksRemaining: number;
  landingCount: number;
  lastLandingTick: number | null;
  footstepDistanceMetres: number;
  footstepSequence: number;
  mantleCount: number;
  mantleProgress: number;
  lastStepHeightMetres: number;
}

export interface PlayerState {
  id: PlayerId;
  position: Vector3;
  velocity: Vector3;
  facingYaw: number;
  lookYaw: number;
  isSprinting: boolean;
  locomotion: PlayerLocomotionState;
}

/**
 * Authoritative, JSON-serializable simulation state.
 *
 * Runtime renderer, DOM, and physics handles must remain in their owning
 * adapters and be indexed by the stable IDs stored here.
 */
export interface GameState {
  schemaVersion: typeof GAME_STATE_SCHEMA_VERSION;
  tick: number;
  simulationTimeSeconds: number;
  playersById: Record<PlayerId, PlayerState>;
}

export function createPlayerState(id: PlayerId): PlayerState {
  return {
    id,
    position: { x: 0, y: 0, z: 0 },
    velocity: { x: 0, y: 0, z: 0 },
    facingYaw: 0,
    lookYaw: 0,
    isSprinting: false,
    locomotion: {
      mode: "grounded",
      grounded: true,
      groundNormal: { x: 0, y: 1, z: 0 },
      groundSlopeRadians: 0,
      surfaceId: null,
      coyoteTicksRemaining: 0,
      jumpBufferTicksRemaining: 0,
      landingResponseTicksRemaining: 0,
      landingCount: 0,
      lastLandingTick: null,
      footstepDistanceMetres: 0,
      footstepSequence: 0,
      mantleCount: 0,
      mantleProgress: 0,
      lastStepHeightMetres: 0,
    },
  };
}

export function createInitialGameState(playerId: PlayerId): GameState {
  return {
    schemaVersion: GAME_STATE_SCHEMA_VERSION,
    tick: 0,
    simulationTimeSeconds: 0,
    playersById: {
      [playerId]: createPlayerState(playerId),
    },
  };
}
